import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { studentApi, batchApi, apiErrorMessage } from '../services/api';
import { useOrg, useReloadOrg } from '../context/OrgContext';
import { useToast } from '../context/ToastContext';
import { Button, Card, PageSkeleton, ErrorState, EmptyState, ConfirmDialog, PageHeader } from '../components/ui';
import StudentDialog from '../components/dialogs/StudentDialog';
import { addStudentBlockedReason, seatUsageLabel } from '../utils/institute';

/// How a pending student got here decides what the owner can do about it.
function statusLabel(student) {
  if (student.status === 'active') return 'Active';
  return student.invitedEmail ? 'Invited' : 'Requested to join';
}

export default function StudentsPage() {
  const org = useOrg();
  const reloadOrg = useReloadOrg();
  const toast = useToast();
  // Seats come from the org payload; the server refuses an invite or approval
  // regardless, this only explains why the buttons are off.
  const addBlocked = addStudentBlockedReason(org);
  const seatLabel = seatUsageLabel(org);
  const [students, setStudents] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [busyStudent, setBusyStudent] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([studentApi.list(org.id), batchApi.list(org.id)])
      .then(([studentsRes, batchesRes]) => {
        setStudents(studentsRes.data || []);
        setBatches(batchesRes.data || []);
        setError(null);
      })
      .catch((err) => setError(apiErrorMessage(err, 'Failed to load students')))
      .finally(() => setLoading(false));
  }, [org.id]);

  useEffect(() => { load(); }, [load]);

  const removeStudent = async () => {
    if (!removeTarget) return;
    setRemoving(true);
    try {
      await studentApi.remove(org.id, removeTarget.userId);
      setStudents((items) => items.filter((s) => s.userId !== removeTarget.userId));
      toast.success('Student removed.');
      setRemoveTarget(null);
      reloadOrg(); // a seat may have freed up
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to remove student'));
    } finally {
      setRemoving(false);
    }
  };

  // Tests are assigned to batches, so a student with none receives nothing.
  const changeBatch = async (student, batchId) => {
    setBusyStudent(student.userId);
    try {
      await studentApi.setBatch(org.id, student.userId, batchId || null);
      const batchName = batches.find((b) => b.id === batchId)?.name;
      setStudents((items) => items.map((s) => (
        s.userId === student.userId ? { ...s, batchId: batchId || null, batchName } : s
      )));
      toast.success(batchId ? `Moved to ${batchName}.` : 'Removed from batch.');
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to change batch'));
    } finally {
      setBusyStudent(null);
    }
  };

  const respondToRequest = async (student, approve) => {
    setBusyStudent(student.userId);
    try {
      if (approve) {
        await studentApi.approve(org.id, student.userId);
        toast.success(`${student.userName || student.userEmail} approved.`);
      } else {
        await studentApi.reject(org.id, student.userId);
        toast.success('Request declined.');
      }
      load();
      reloadOrg();
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to update the request'));
    } finally {
      setBusyStudent(null);
    }
  };

  if (loading) return <PageSkeleton />;
  if (error) return <ErrorState message={error} />;

  return (
    <>
      <PageHeader
        eyebrow="Student management"
        title="Students"
        subtitle="Invite students, put them in a batch, and approve anyone who joined with your institute code."
        action={(
          <div className="seat-actions">
            {seatLabel && <span className="seat-usage">{seatLabel}</span>}
            <Button disabled={Boolean(addBlocked)} title={addBlocked || undefined} onClick={() => setDialogOpen(true)}>
              + Invite student
            </Button>
          </div>
        )}
      />
      {addBlocked && <p className="helper seat-blocked">{addBlocked}</p>}
      <Card className="table-card">
        <table>
          <thead><tr><th>Student</th><th>Batch</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {students.length ? students.map((s) => {
              const isPendingRequest = s.status !== 'active' && !s.invitedEmail;
              return (
                <tr key={s.userId}>
                  <td><Link to={`/students/${s.userId}`}><b>{s.userName}</b></Link><small>{s.userEmail}</small></td>
                  <td>
                    <select
                      value={s.batchId || ''}
                      disabled={busyStudent === s.userId}
                      onChange={(e) => changeBatch(s, e.target.value)}
                    >
                      <option value="">Unassigned</option>
                      {batches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                    </select>
                  </td>
                  <td>
                    <span className={s.status === 'active' ? 'status' : 'pending'}>
                      {statusLabel(s)}
                    </span>
                  </td>
                  <td>
                    {isPendingRequest && (
                      <>
                        <Button
                          variant="text"
                          disabled={busyStudent === s.userId || Boolean(addBlocked)}
                          title={addBlocked || undefined}
                          onClick={() => respondToRequest(s, true)}
                        >
                          Approve
                        </Button>
                        <Button
                          variant="text"
                          disabled={busyStudent === s.userId}
                          onClick={() => respondToRequest(s, false)}
                        >
                          Decline
                        </Button>
                      </>
                    )}
                    <Button variant="text" onClick={() => setRemoveTarget(s)}>Remove</Button>
                  </td>
                </tr>
              );
            }) : (
              <tr><td colSpan="4">
                <EmptyState title="No students yet" action={<Button variant="secondary" disabled={Boolean(addBlocked)} onClick={() => setDialogOpen(true)}>Invite your first student</Button>}>
                  Students you invite will appear here with their batch and activity status.
                </EmptyState>
              </td></tr>
            )}
          </tbody>
        </table>
      </Card>
      {!batches.length && students.length > 0 && (
        <p className="helper">
          Create a batch before scheduling tests: tests are assigned to a batch, so students who are
          not in one cannot receive any.
        </p>
      )}
      {dialogOpen && (
        <StudentDialog
          batches={batches}
          onClose={() => setDialogOpen(false)}
          onInvited={() => { setDialogOpen(false); load(); reloadOrg(); }}
        />
      )}
      {removeTarget && (
        <ConfirmDialog
          title="Remove student?"
          message={`${removeTarget.userName || removeTarget.userEmail} will lose access to your institute's quizzes and content. This does not delete their Prepgen.ai account.`}
          confirmLabel="Remove student"
          busy={removing}
          onConfirm={removeStudent}
          onCancel={() => setRemoveTarget(null)}
        />
      )}
    </>
  );
}
